import classNames from "classnames";
import { ContextMenu, useContextMenu } from "../ContextMenu/ContextMenu";
import {
	computeActionsToRender
} from "../ActionList/actionsRenderHelpers";
import {
	useCallbackWithoutArguments
} from "client/Hooks";
import {
	ActionAndOthers
} from "client_legacy/ndaw/actions";
import {
	createContext,
	MouseEvent,
	PropsWithChildren,
	ReactElement,
	Ref,
	RefObject,
	useCallback,
	useMemo,
	useRef,
	useState,
	KeyboardEvent,
	FocusEvent
} from "react";
import styles from "./GridItem.module.less";
import {
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import { GridItemPreviewContent } from "./GridItemPreviewContent";
import { GridItemContent } from "./GridItemContent";
import { mergeRefs } from "client/Misc/reactUtils";
import { tryResolveKeyboardKey } from "client/Misc/keyboardUtils";
import { Key } from "client/Misc/commonTypes";

export type GridItemProps<TActionContext>= {
	isActive?: boolean;
	isTouch: boolean;
	hasError?: boolean;
	isDisabled?: boolean;
	isReadonly?: boolean;
	trackingId: string;
	onDoubleClick?(): void;
	onClick?: (args: GridItemMouseEvent) => boolean | void;
	onKeyDown?: (args: KeyboardEvent<HTMLDivElement>) => boolean;
	onTouchStart?: React.TouchEventHandler;
	onTouchMove?: React.TouchEventHandler;
	onTouchEnd?: React.TouchEventHandler;
	tooltip?: string;
	tabIndex?: number;
	ref?: Ref<HTMLDivElement>;
} & ({
	actions?: undefined;
	onContextMenu?(args: MouseEvent): void;
} | {
	actions: readonly ActionAndOthers<TActionContext>[];
	actionContext: TActionContext;
	actionsTitle: string;
	onContextMenu?: undefined;
});

export const GridItem = <TActionContext,>(props: PropsWithChildren<GridItemProps<TActionContext>>): ReactElement => {
	const {
		hasError,
		isDisabled = false,
		isReadonly = false,
		isActive = false,
		isTouch,
		trackingId,
		tooltip,
		onClick,
		onDoubleClick,
		onContextMenu,
		onTouchEnd,
		onTouchMove,
		onTouchStart,
		children,
		tabIndex,
		ref,
		onKeyDown
	} = props;

	const [isHovered, setIsHovered] = useState<boolean>(false);
	const [isFocused, setIsFocused] = useState<boolean>(false);

	const itemRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const doubleClickHandler: (() => void) | undefined = useCallbackWithoutArguments(onDoubleClick);
	const [internalContextMenuHandler, contextMenuProps] = useContextMenu(itemRef);

	const actionsToRender = useMemo(
		(): readonly ActionAndOthers<TActionContext>[] | null => {
				if (!props.actions) {
					return null;
				}

				return computeActionsToRender(
					props.actions,
					props.actionContext
				);
			},
		[props]
	);

	const contextData = useMemo(
		(): GridItemContextData => ({
			isInteracting: !isReadonly
				&& (isHovered
					|| isActive
					|| isTouch
				),
			isFocused,
			isDisabled
		}),
		[
			isActive,
			isHovered,
			isReadonly,
			isTouch,
			isDisabled,
			isFocused
		]
	);

	const contextMenuHandler = useCallback(
		(args: MouseEvent): void => {
			if (onContextMenu) {
				args.preventDefault();
				onContextMenu(args);
				return;
			}

			internalContextMenuHandler(args);
		},
		[
			internalContextMenuHandler,
			onContextMenu
		]
	);

	const clickHandler = useMemo(
		(): ((args: MouseEvent<HTMLDivElement>) => void) | undefined => {
			if (!onClick
				|| isDisabled
				|| isReadonly
			) {
				return undefined;
			}

			return (args: MouseEvent<HTMLDivElement>): void => {
				const result: boolean | void = onClick({
					modifiers: {
						shift: args.shiftKey,
						ctrl: args.ctrlKey
					}
				});

				if (result) {
					args.preventDefault();
				}
			};
		},
		[
			isDisabled,
			isReadonly,
			onClick
		]
	);

	const keyDownHandler = useMemo(
		(): ((args: KeyboardEvent<HTMLDivElement>) => void) | undefined => {
			if (isDisabled || isReadonly) {
				return undefined;
			}

			return (args: KeyboardEvent<HTMLDivElement>): void => {
				if (isNotNil(onKeyDown)) {
					const result: boolean = onKeyDown(args);

					if (result) {
						args.preventDefault();
						return;
					}
				}

				if (args.target !== args.currentTarget) {
					return;
				}

				const key: Key | null = tryResolveKeyboardKey(args);

				switch (key) {
					case Key.Enter:
						if (onDoubleClick) {
							args.preventDefault();

							onDoubleClick();

							return;
						}

						if (onClick) {
							args.preventDefault();

							onClick({
								modifiers: {
									shift: args.shiftKey,
									ctrl: args.ctrlKey
								}
							});
						}

						return;
					case Key.Space:
						if (onClick) {
							args.preventDefault();

							onClick({
								modifiers: {
									shift: args.shiftKey,
									ctrl: args.ctrlKey
								}
							});
						}

						return;
					default:
						break;
				}
			};
		},
		[
			onKeyDown,
			isDisabled,
			isReadonly,
			onDoubleClick,
			onClick
		]
	);

	const renderActions = (): ReactElement | null => {
		if (!props.actions
				|| !actionsToRender
				|| actionsToRender.length === 0
		) {
			return null;
		}

		return (
			<>
				{contextMenuProps && (
					<ContextMenu
						{...contextMenuProps}
						actions={actionsToRender}
						context={props.actionContext}
						trackingId="grid-item-context-menu"
						title={props.actionsTitle}
					/>
				)}
			</>
		);
	};

	return (
		<>
			<div
				{...setTrackingId(trackingId)}
				ref={ref ? mergeRefs(ref, itemRef) : itemRef}
				tabIndex={tabIndex}
				className={classNames(
					styles.root,
					{
						[styles.root_hasError]: hasError,
						[styles.root_isDisabled]: isDisabled,
						[styles.root_readonly]: isReadonly,
						[styles.root_active]: isActive,
						[styles.root_hasDoubleClick]: props.onDoubleClick,
					}
				)}
				onClick={clickHandler}
				onDoubleClick={!isDisabled && !isReadonly
					? doubleClickHandler
					: undefined
				}
				onContextMenu={contextMenuHandler}
				onMouseEnter={()=> setIsHovered(true)}
				onMouseLeave={()=> setIsHovered(false)}
				onFocus={() => setIsFocused(true)}
				onBlur={(e: FocusEvent<HTMLDivElement>): void => {
					if (!e.currentTarget.contains(e.relatedTarget)) {
						setIsFocused(false);
					}
				}}
				onTouchStart={onTouchStart}
				onTouchEnd={onTouchEnd}
				onTouchMove={onTouchMove}
				title={tooltip}
				onKeyDown={keyDownHandler}
			>
				<GridItemContext.Provider value={contextData}>
					{children}
				</GridItemContext.Provider>
			</div>
			{renderActions()}
		</>
	);
};
GridItem.displayName = "GridItem";

type GridItemContextData = {
	isInteracting: boolean;
	isFocused: boolean;
	isDisabled: boolean;
};

export const GridItemContext = createContext<GridItemContextData>({
	isInteracting: false,
	isFocused: false,
	isDisabled: false
});

export type GridItemMouseEvent = {
	modifiers: {
		shift: boolean;
		ctrl: boolean;
	};
};

GridItem.Content = GridItemContent;
GridItem.PreviewContent = GridItemPreviewContent;